import { Link } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import { Button } from '@/components/ui/button'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Spinner } from '@/components/ui/spinner'
import { usePart } from '@/features/parts/api'
import { MovementDialog } from '@/features/stock/MovementDialog'
import { invalidateStock, type MovementKind } from '@/features/stock/api'
import { formatAmount } from '@/lib/quantity'

import type { ScanResult } from './api'

/**
 * What to do with something that was scanned but is not simply a page.
 *
 * A lot means someone is standing at the shelf with the drawer open, so the
 * useful thing to offer is taking stock out of it, not a page about it.
 */
export function ScanResultSheet({ result, onClose }: { result: ScanResult; onClose: () => void }) {
  return (
    <Sheet open onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="bottom" className="max-h-[85vh] overflow-y-auto">
        {result.kind === 'part_lot' && result.id ? (
          <LotBody lotId={result.id} partId={result.part_id ?? null} onClose={onClose} />
        ) : result.kind === 'vendor_label' ? (
          <VendorBody result={result} onClose={onClose} />
        ) : (
          <>
            <SheetHeader>
              <SheetTitle>Nothing matched that code</SheetTitle>
              <SheetDescription>
                It is not one of this inventory&apos;s labels, and no distributor label could be
                read from it.
              </SheetDescription>
            </SheetHeader>
            <div className="p-4">
              <Button variant="outline" onClick={onClose}>
                Close
              </Button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  )
}

function LotBody({
  lotId,
  partId,
  onClose,
}: {
  lotId: string
  partId: string | null
  onClose: () => void
}) {
  const queryClient = useQueryClient()
  const part = usePart(partId ?? '')
  const [kind, setKind] = useState<MovementKind | null>(null)

  if (!partId || part.isPending) {
    return (
      <SheetHeader>
        <SheetTitle>Stock lot</SheetTitle>
        <SheetDescription>
          <Spinner label="Looking it up" />
        </SheetDescription>
      </SheetHeader>
    )
  }

  const lot = part.data?.lots.find((candidate) => candidate.id === lotId)
  if (!part.data || !lot) {
    return (
      <SheetHeader>
        <SheetTitle>That lot is gone</SheetTitle>
        <SheetDescription>The label refers to stock that no longer exists.</SheetDescription>
      </SheetHeader>
    )
  }

  const refresh = () => invalidateStock(queryClient)

  return (
    <>
      <SheetHeader>
        <SheetTitle>{part.data.name}</SheetTitle>
        <SheetDescription>
          {lot.location_path.map((segment) => segment.name).join(' / ') || 'No location'} ·{' '}
          {lot.amount_unknown ? 'amount unknown' : formatAmount(lot.amount, part.data.unit_symbol)}
        </SheetDescription>
      </SheetHeader>

      <div className="flex flex-wrap gap-2 p-4">
        <Button onClick={() => setKind('withdraw')}>Take stock</Button>
        <Button variant="outline" onClick={() => setKind('add')}>
          Add stock
        </Button>
        <Button variant="outline" onClick={() => setKind('set')}>
          Correct the count
        </Button>
        <Button asChild variant="ghost" onClick={onClose}>
          <Link to="/parts/$partId" params={{ partId: part.data.id }}>
            Open the part
          </Link>
        </Button>
      </div>

      {kind && (
        <MovementDialog
          open
          onOpenChange={(open) => !open && setKind(null)}
          kind={kind}
          lot={{
            id: lot.id,
            amount: lot.amount,
            amount_unknown: lot.amount_unknown,
            location_id: lot.location_id ?? null,
          }}
          unitSymbol={part.data.unit_symbol}
          wholeUnitsOnly={part.data.unit_is_integer}
          onDone={async () => {
            await refresh()
            setKind(null)
            onClose()
          }}
        />
      )}
    </>
  )
}

function VendorBody({ result, onClose }: { result: ScanResult; onClose: () => void }) {
  const vendor = result.vendor
  const quantity = vendor?.quantity ?? null

  return (
    <>
      <SheetHeader>
        <SheetTitle>A distributor label</SheetTitle>
        <SheetDescription>
          {vendor?.mpn || vendor?.spn || 'No part number on the label'}
          {vendor?.manufacturer ? ` · ${vendor.manufacturer}` : ''}
          {quantity !== null ? ` · ${quantity} in the bag` : ''}
        </SheetDescription>
      </SheetHeader>

      <div className="flex flex-wrap gap-2 p-4">
        {result.matched_part_id ? (
          <Button asChild onClick={onClose}>
            <Link to="/parts/$partId" params={{ partId: result.matched_part_id }}>
              Add to {result.matched_part_name}
            </Link>
          </Button>
        ) : (
          <Button asChild onClick={onClose}>
            {/* An LCSC label names the exact product; its C-code is what the
                LCSC provider answers, where an MPN search would come up empty.
                Every other label leads with the MPN for the keyword search. */}
            <Link
              to="/providers"
              search={{
                q:
                  (vendor?.format === 'lcsc'
                    ? vendor?.spn || vendor?.mpn
                    : vendor?.mpn || vendor?.spn) || '',
              }}
            >
              Look it up at a distributor
            </Link>
          </Button>
        )}
        <Button variant="ghost" onClick={onClose}>
          Close
        </Button>
      </div>
    </>
  )
}
